import { useEffect, useState } from "react";
import api, { track } from "@/lib/api";
import VideoPlayer from "@/components/VideoPlayer";
import {
  PlayCircle,
  Play,
  Clock,
  BookOpen,
  HandsPraying,
  MusicNotes,
  Microphone,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const categoryIcons = {
  Worship: MusicNotes,
  Teaching: BookOpen,
  Testimony: Microphone,
  Prayer: HandsPraying,
};

export default function WatchPage() {
  const [data, setData] = useState({ live: [], featured: [], continue_watching: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [playing, setPlaying] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/watch/content");
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openPlayer = async (item) => {
    setPlaying(item);
    track("watch_played", { id: item.id, slug: item.slug, title: item.title, category: item.category });
    try {
      await api.post(`/watch/content/${item.id}/progress`, { progress: Math.max(item.progress || 5, 5) });
      load();
    } catch { /* ignore */ }
  };
  const closePlayer = () => setPlaying(null);

  const recordProgress = async (id, progress) => {
    try {
      await api.post(`/watch/content/${id}/progress`, { progress });
      toast.success("Progress saved");
      load();
    } catch {
      // ignore
    }
  };

  const live = data.live[0];
  const categories = ["All", ...Array.from(new Set(data.all.map((c) => c.category)))];
  const filtered = data.all.filter((c) => !c.is_live && (activeCategory === "All" || c.category === activeCategory));

  return (
    <div className="px-5 md:px-8 py-4 max-w-4xl mx-auto" data-testid="watch-page">
      {/* Live Card */}
      {live && (
        <div className="glass-card-strong p-6 mb-6 relative overflow-hidden text-center" data-testid="live-card">
          <div className="spiritual-orb-sm w-32 h-32 -top-4 right-0 animate-orb" />
          <div className="relative z-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-500 text-[10px] font-body font-medium mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE
            </span>
            <h2 className="font-heading text-2xl text-[#2D1B0E] font-medium mb-1">{live.title}</h2>
            <p className="text-[#8B7355] font-body text-sm mb-4">{live.description || "Join thousands watching now"}</p>
            <button
              onClick={() => openPlayer(live)}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full glass-card text-[#5C4033] font-body text-sm font-medium hover:bg-white/60 transition-all"
              data-testid="watch-live-btn"
            >
              <PlayCircle size={18} weight="fill" /> Watch Live
            </button>
          </div>
        </div>
      )}

      {/* Continue Watching */}
      {data.continue_watching.length > 0 && (
        <div className="mb-6">
          <h2 className="font-heading text-xl text-[#2D1B0E] font-medium mb-4">Continue Watching</h2>
          <div className="space-y-3">
            {data.continue_watching.map((v) => (
              <button
                key={v.id}
                onClick={() => openPlayer(v)}
                className="w-full glass-card p-4 flex items-center gap-4 text-left cursor-pointer transition-all hover:-translate-y-0.5"
                data-testid={`continue-${v.slug}`}
              >
                <div className="w-14 h-14 rounded-xl bg-[#EDE6DC] flex items-center justify-center flex-shrink-0">
                  <Play size={20} weight="fill" className="text-[#5C4033]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-medium text-[#2D1B0E] truncate">{v.title}</p>
                  <p className="font-body text-[10px] text-[#8B7355]">{v.speaker} — {v.duration}</p>
                  <div className="w-full h-1 bg-[#EDE6DC] rounded-full mt-2">
                    <div className="h-1 bg-[#C4A265] rounded-full" style={{ width: `${v.progress}%` }} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="mb-6">
        <h2 className="font-heading text-xl text-[#2D1B0E] font-medium mb-4">Categories</h2>
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
          {categories.map((cat) => {
            const Icon = categoryIcons[cat] || BookOpen;
            const active = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`cat-${cat.toLowerCase()}`}
                className={`glass-card px-5 py-3 flex items-center gap-2 transition-all whitespace-nowrap ${
                  active ? "bg-white/80 ring-1 ring-[#C4A265]" : "hover:-translate-y-0.5"
                }`}
              >
                {cat !== "All" && <Icon size={18} weight="duotone" className="text-[#5C4033]" />}
                <span className="font-body text-sm text-[#2D1B0E]">{cat}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* All Content */}
      <div>
        <h2 className="font-heading text-xl text-[#2D1B0E] font-medium mb-4">Sermons & Teachings</h2>
        {loading ? (
          <div className="glass-card p-6 text-center text-[#8B7355] text-sm font-body">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => openPlayer(s)}
                className="w-full glass-card p-4 text-left cursor-pointer transition-all hover:-translate-y-0.5"
                data-testid={`sermon-${s.slug}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-xl ${s.color || "bg-[#EDE6DC]"} flex items-center justify-center flex-shrink-0`}>
                    <Play size={16} weight="fill" className="text-[#5C4033]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm font-medium text-[#2D1B0E]">{s.title}</p>
                    <p className="font-body text-[10px] text-[#8B7355]">{s.speaker}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock size={10} className="text-[#B8A088]" />
                      <span className="font-body text-[10px] text-[#B8A088]">{s.duration}</span>
                      <span className="font-body text-[10px] text-[#C4A265]">{s.category}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <VideoPlayer open={!!playing} onClose={closePlayer} content={playing} />
    </div>
  );
}
